import React from "react";
import LoggedOutPage from "./LoggedOutPage";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { useSearchParams } from "react-router-dom";
import { defineMessage, FormattedMessage, MessageDescriptor } from "react-intl";
import { Box, Typography } from "@mui/material";

const ERROR_TYPES = ["UNAUTHORIZED", "NOT_FOUND", "MISSING_CLIENT_CERTIFICATE", "CERTIFICATE_NOT_ACCEPTABLE"] as const;
export type ErrorType = typeof ERROR_TYPES[number];

const translations: Record<ErrorType, {title: MessageDescriptor; message: MessageDescriptor}> = {
    UNAUTHORIZED: {
        title: defineMessage({
            id: "error_page.title.unauthorized",
            defaultMessage: "Unauthorized"
        }),
        message: defineMessage({
            id: "error_page.unauthorized_to_access_page",
            defaultMessage: "You are not authorized to access this page"
        })
    },
    NOT_FOUND: {
        title: defineMessage({
            id: "error_page.title.not_found",
            defaultMessage: "Not found"
        }),
        message: defineMessage({
            id: "error_page.not_found",
            defaultMessage: "Page not found"
        })
    },
    MISSING_CLIENT_CERTIFICATE: {
        title: defineMessage({
            id: "error_page.title.client_cert",
            defaultMessage: "Missing client certificate"
        }),
        message: defineMessage({
            id: "error_page.client_cert",
            defaultMessage: "To provide different client certification, the browser must be restarted."
        })
    },
    CERTIFICATE_NOT_ACCEPTABLE: {
        title: defineMessage({
            id: "error_page.title.client_cert",
            defaultMessage: "The certificate is not acceptable"
        }),
        message: defineMessage({
            id: "error_page.client_cert",
            defaultMessage: "To provide different client certification, the browser must be restarted."
        })
    }
}

export enum IconType {
    NONE = "NONE",
    WARNING = "WARNING",
}

const iconTypeMapping = {
    NONE: undefined,
    WARNING: (
        <FontAwesomeIcon icon={faExclamationTriangle} style={{ fontSize: 24, color: "#ff7700", marginRight: 16 }} />
    ),
    MISSING_CLIENT_CERTIFICATE: undefined,
    CERTIFICATE_NOT_ACCEPTABLE: undefined,
};

type ErrorPageProps = {
    type: ErrorType | "fromQueryParam";
    iconType?: IconType;
};

const ErrorPage = ({ type, iconType = IconType.WARNING }: ErrorPageProps) => {
    const [searchParams] = useSearchParams();

    if (type === "fromQueryParam") {
        const searchParamValue = searchParams.get("type")?.toUpperCase() || null;
        if (searchParamValue && ERROR_TYPES.includes(searchParamValue as ErrorType)) {
            type = searchParamValue as ErrorType;
        } else {
            console.error("Invalid error type in query param");
            type = "NOT_FOUND";
        }
    }

    return (
        <LoggedOutPage title={<FormattedMessage {...translations[type].title} />}>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "100px",
                    width: "100%",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        p: 3,
                        borderRadius: 1,
                        bgcolor: "background.paper",
                    }}
                >
                    {iconTypeMapping[iconType]}
                    <Typography variant="h6" component="div">
                        <FormattedMessage {...translations[type].message} />
                    </Typography>
                </Box>
            </Box>
        </LoggedOutPage>
    );
};

export default ErrorPage;
